import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import swal from "sweetalert";
import {
  applyClassToCharacters,
  customOption,
  LoadingSpinner,
} from "../../../constants/const";
import { addMember } from "../../member/ApiMember";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles, compressImageFile } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { getMinMaxDates, validateTransactionDate } from "../fiscalYearHelper";

const getDateValue = (value) => {
  if (!value) return new Date();
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  const parts = raw.split(/[-/]/).map(Number);

  if (parts.length === 3 && parts.every((part) => !Number.isNaN(part))) {
    if (String(parts[0]).length === 4) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }

  const parsedDate = new Date(raw);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const getAmount = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(Math.abs(Number(String(value).replace(/,/g, ""))));
};

const findOption = (options, values) =>
  options.find((option) => {
    const optionValues = [
      option?.value,
      option?.id,
      option?.label,
      option?.label1,
      option?.name,
      option?.title,
    ].map((item) => String(item || "").trim().toLowerCase());

    return values.some((value) =>
      optionValues.includes(String(value || "").trim().toLowerCase()),
    );
  }) || null;

const CustomInput = ({ label, value, onChange, error }) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Input type="text" value={value} onChange={onChange} className="no-drag" />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

const EditOtherIncomeLoanFromMarkaz = ({
  setModal,
  transactionNo,
  transaction,
  unitId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [unitBankAccounts, setUnitBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [formData, setFormData] = useState({
    selectedFinanceCategory: transaction?.category || null,
    selectedAccount: null,
    date: getDateValue(transaction?.date),
    amount: getAmount(transaction?.amount),
    memo: transaction?.memo || "",
    reference: transaction?.reference || "",
  });
  const [errors, setErrors] = useState({});
  const [attachedFile, setAttachedFile] = useState(null);
  const [loadingView, setLoadingView] = useState(false);
  const [loading, setLoading] = useState({
    bankAccounts: false,
    accounts: false,
    submit: false,
  });

  const existingFileId = transaction?.fileId || transaction?.file_id || (Array.isArray(transaction?.fileIds) && transaction.fileIds[0]) || (Array.isArray(transaction?.file_ids) && transaction.file_ids[0]) || null;

  const isBusy = Object.values(loading).some(Boolean);
  const toggle = () => setModal(false);

  const handleDrag = (e, ui) => {
    setPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 1500 * 1024; // 1.5 MB
    let processedFile = file;

    if (file.type && file.type.startsWith("image/") && file.size > MAX_SIZE) {
      try {
        processedFile = await compressImageFile(file, 1400 * 1024);
      } catch (err) {
        console.error("Image compression error:", err);
      }
    }

    if (processedFile.size > MAX_SIZE) {
      setAttachedFile(null);
      setErrors((prev) => ({
        ...prev,
        attachedFile: <GetLanguageString props="halqa_loan_letter_size_limit" />,
      }));
      e.target.value = "";
    } else {
      setAttachedFile(processedFile);
      setErrors((prev) => ({ ...prev, attachedFile: "" }));
    }
  };

  const handleViewExistingAttachment = () => {
    if (!existingFileId) return;
    setLoadingView(true);
    ApiFinanceLocal()
      .ViewAttachmentByFileId(existingFileId)
      .then((res) => {
        const file = new Blob([res.data], {
          type: res.headers["content-type"] || "application/pdf",
        });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: "Unable to view attachment file.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoadingView(false));
  };

  useEffect(() => {
    if (!unitId) return;

    setLoading((prev) => ({ ...prev, bankAccounts: true }));
    addMember()
      .GetUnitBankAccounts(unitId)
      .then((res) => {
        const list = res.data || [];
        setUnitBankAccounts(list);
        setSelectedBankAccount(
          findOption(list, [
            transaction?.rafiqBankId,
            transaction?.bankAccountId,
            transaction?.bankName,
          ]) ||
          list[0] ||
          null,
        );
      })
      .catch((err) => console.error(err))
      .finally(() =>
        setLoading((prev) => ({ ...prev, bankAccounts: false })),
      );
  }, [transaction, unitId]);

  useEffect(() => {
    if (!transaction) return;
    const memoVal = transaction.memo || transaction.lineMemo || (Array.isArray(transaction.details) && transaction.details[0]?.memo) || (Array.isArray(transaction.details) && transaction.details[0]?.lineMemo) || "";
    setFormData((prev) => ({
      ...prev,
      selectedFinanceCategory: transaction.category || prev.selectedFinanceCategory,
      date: getDateValue(transaction.date),
      amount: getAmount(transaction.amount),
      memo: memoVal,
      reference: transaction.reference || "",
    }));
  }, [transaction]);

  useEffect(() => {
    const categoryId = formData.selectedFinanceCategory?.value;
    if (!categoryId) return;

    setLoading((prev) => ({ ...prev, accounts: true }));
    ApiFinance()
      .GetFinanceSubCategoryAccountsById(categoryId)
      .then((res) => {
        const accounts = res.data || [];
        setFormData((prev) => ({
          ...prev,
          selectedAccount:
            findOption(accounts, [
              transaction?.accountId,
              transaction?.financeAccount?.value,
              transaction?.financeAccount?.id,
            ]) ||
            accounts[0] ||
            null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to load income heads.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, accounts: false })));
  }, [formData.selectedFinanceCategory, transaction]);

  const validate = () => {
    const nextErrors = {
      amount: formData.amount ? (
        ""
      ) : (
        <GetLanguageString props="funds_add_fund_amount_paid_error" />
      ),
    };

    const dateError = validateTransactionDate(formData.date, transaction?.date);
    if (dateError) {
      nextErrors.date = t(dateError);
    }

    if (errors.attachedFile) {
      nextErrors.attachedFile = errors.attachedFile;
    }

    setErrors(nextErrors);
    return Object.values(nextErrors).every((value) => value === "");
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;

    const payload = new FormData();
    payload.append("transactionNo", transactionNo || transaction?.transactionId || transaction?.transNo || 0);
    payload.append("rafiqBankId", selectedBankAccount?.value ?? 0);
    payload.append("date", formData.date ? (formData.date instanceof Date ? formData.date.toISOString() : new Date(formData.date).toISOString()) : new Date().toISOString());
    payload.append("amount", formData.amount);
    payload.append("memo", formData.memo);
    payload.append("reference", formData.reference || "");
    payload.append("unitId", unitId || transaction?.unitId || 0);
    if (formData.selectedAccount?.value) {
      payload.append("financeAccount.value", formData.selectedAccount.value);
    }
    if (formData.selectedFinanceCategory?.label1) {
      payload.append("category.label1", formData.selectedFinanceCategory.label1);
    }
    if (formData.selectedFinanceCategory?.value) {
      payload.append("category.value", formData.selectedFinanceCategory.value);
    }
    if (attachedFile) {
      payload.append("File", attachedFile);
    }

    setLoading((prev) => ({ ...prev, submit: true }));
    ApiFinanceLocal()
      .RequestModificationOtherIncomeLoanFromMarkaz(payload)
      .then((res) => {
        const data = res.data || {};
        if (Number(data.statusCode) !== 200) {
          const isWarning = Number(data.statusCode) === 300 || data.message === "modification_request_already_pending";
          swal({
            title: isWarning ? t("common_warning") || "Warning" : "Error",
            text: t(data.message) || data.message || "Failed to update Other Income",
            icon: isWarning ? "warning" : "error",
            buttons: "OK",
          }).then(() => {
            if (isWarning) {
              setModal(false);
            }
          });
          return;
        }

        swal({
          title: "Success",
          text: t(data.message) || t("member_member_detail_fund_edited"),
          icon: "success",
          buttons: "OK",
        }).then(() => {
          setModal(false);
          if (typeof onSuccess === "function") onSuccess();
        });
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to update Other Income.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, submit: false })));
  };

  return (
    <Draggable position={position} onDrag={handleDrag} handle=".modal-header" cancel=".no-drag">
      <Modal isOpen className="inmodal" size="lg" autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          <GetLanguageString props="funds_other_income_loan_from_markaz" />
        </ModalHeader>

        {isBusy ? (
          <ModalBody className="text-center">
            <LoadingSpinner />
          </ModalBody>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            <ModalBody>
              <Row>
                <Col md="12" className="mb-3">
                  <div style={{ background: "#FFF8E1", border: "1px solid #FFD54F", borderRadius: "8px", padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px" }}>
                    <i className="fa fa-info-circle" style={{ color: "#F57F17", fontSize: "18px" }}></i>
                    <span style={{ fontSize: "13px", color: "#5D4037" }}>
                      {t("amanat_edit_modification_notice")}
                    </span>
                  </div>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="fund_bank_account" />
                    </Label>
                    <Select
                      components={{ Option: customOption }}
                      options={unitBankAccounts}
                      value={
                        selectedBankAccount
                          ? {
                            value: selectedBankAccount.value,
                            label: applyClassToCharacters(
                              selectedBankAccount.label,
                              "ur",
                            ),
                          }
                          : null
                      }
                      onChange={setSelectedBankAccount}
                      placeholder={t("member_select")}
                      classNamePrefix="select"
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_date" />
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      minDate={getMinMaxDates(formData.date, transaction?.date).minDate}
                      maxDate={getMinMaxDates(formData.date, transaction?.date).maxDate}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.date && (
                      <h4 className="text-error">{errors.date}</h4>
                    )}
                  </FormGroup>
                </Col>
                <Col md="3">
                  <CustomInput
                    label="funds_add_fund_amount"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", e.target.value)}
                    error={errors.amount}
                  />
                </Col>
                <Col md="6">
                  <CustomInput
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>

                {/* File Upload Field for Halqa Loan Letter */}
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      {existingFileId ? (
                        <>
                          <GetLanguageString props="halqa_loan_letter" />
                          <span className="text-muted ms-1" style={{ fontSize: "11.5px", fontWeight: "normal" }}>
                            (<GetLanguageString props="optional_replaces_existing" />, Max 1.5MB)
                          </span>
                        </>
                      ) : (
                        <GetLanguageString props="halqa_loan_letter_attachment" />
                      )}
                    </Label>
                    <Input
                      type="file"
                      accept="image/*,application/pdf"
                      name="file"
                      className="form-control no-drag"
                      onChange={handleFileChange}
                      invalid={!!errors.attachedFile}
                    />
                    {errors.attachedFile && (
                      <h4 className="text-error mt-1">{errors.attachedFile}</h4>
                    )}
                  </FormGroup>
                </Col>

                {/* Existing Attachment Display if present */}
                {existingFileId && (
                  <Col md="12" className="mb-2">
                    <div
                      style={{
                        padding: "14px 18px",
                        backgroundColor: "#ffffff",
                        borderRadius: "8px",
                        border: "1px solid #e7eaec",
                        borderLeft: "4px solid #0EA5E9",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13.5px",
                          fontWeight: "600",
                          color: "#0284C7",
                          marginBottom: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <i className="fa fa-paperclip" style={{ fontSize: "15px" }}></i>
                        <span>
                          <GetLanguageString props="attached_files" /> (1)
                        </span>
                      </div>
                      <div
                        className="d-flex justify-content-between align-items-center p-2 rounded border bg-light"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <i className="fa fa-file text-muted"></i>
                          <div>
                            <div style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}>
                              <GetLanguageString props="halqa_loan_letter_already_attached" />
                            </div>
                            <div style={{ fontSize: "11px", color: "#6B7280" }}>
                              <GetLanguageString props="uploading_new_file_replaces_current_notice" />
                            </div>
                          </div>
                        </div>
                        <Button
                          color="white"
                          size="sm"
                          style={{
                            padding: "6px 14px",
                            fontSize: "12px",
                            fontWeight: "500",
                            borderRadius: "6px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            border: "1px solid #D1D5DB",
                          }}
                          disabled={loadingView}
                          onClick={handleViewExistingAttachment}
                          type="button"
                        >
                          {loadingView ? (
                            <Spinner size="sm" style={{ width: "12px", height: "12px" }} />
                          ) : (
                            <i className="fa fa-eye text-primary"></i>
                          )}
                          <GetLanguageString props="member_add_rafiq_request_view_document" />
                        </Button>
                      </div>

                      {attachedFile && (
                        <div
                          className="d-flex align-items-center justify-content-between p-2 mt-2 rounded"
                          style={{
                            backgroundColor: "#ECFDF5",
                            border: "1px solid #A7F3D0",
                            fontSize: "12px",
                            color: "#065F46",
                          }}
                        >
                          <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "80%" }}>
                            <i className="fa fa-file text-success"></i>
                            <span className="text-truncate fw-bold">{attachedFile.name}</span>
                            <span className="badge bg-light text-muted border px-1">
                              {(attachedFile.size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                          <Button
                            color="link"
                            size="sm"
                            className="text-danger p-0 ms-2 text-decoration-none"
                            onClick={() => setAttachedFile(null)}
                            title={t("remove_file") || "Remove file"}
                            type="button"
                          >
                            <i className="fa fa-times"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </Col>
                )}

                {!existingFileId && attachedFile && (
                  <Col md="12" className="mb-2">
                    <div
                      className="d-flex align-items-center justify-content-between p-2 rounded"
                      style={{
                        backgroundColor: "#ECFDF5",
                        border: "1px solid #A7F3D0",
                        fontSize: "12px",
                        color: "#065F46",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "80%" }}>
                        <i className="fa fa-file text-success"></i>
                        <span className="text-truncate fw-bold">{attachedFile.name}</span>
                        <span className="badge bg-light text-muted border px-1">
                          {(attachedFile.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                      <Button
                        color="link"
                        size="sm"
                        className="text-danger p-0 ms-2 text-decoration-none"
                        onClick={() => setAttachedFile(null)}
                        title={t("remove_file") || "Remove file"}
                        type="button"
                      >
                        <i className="fa fa-times"></i>
                      </Button>
                    </div>
                  </Col>
                )}
              </Row>
            </ModalBody>

            <ModalFooter>
              <Button
                color="white"
                className={GetCustomStyles().btn_style}
                onClick={toggle}
              >
                <GetLanguageString props="common_cancel" />
              </Button>
              <Button
                color="warning"
                className={GetCustomStyles().btn_style}
                disabled={isBusy}
                type="submit"
              >
                <GetLanguageString props="submit_modification_request" />
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Draggable>
  );
};

export default EditOtherIncomeLoanFromMarkaz;
